import { useFormik } from "formik";
import { Box, Button, FormControl, Typography } from "@mui/material";
import { InputBase, InputLabel } from "components/common/Input";
import { Modal } from "components/common/Modal";
import useVendorContact, { VendorContactPayload } from "hooks/useVendorContact";
import { LogisticsTicketVendor } from "types/logisticsTicket";
import * as yup from "yup";

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
  vendorUuid: string;
  vendorName: string;
  contactName?: string;
  email?: string;
  phone?: string;
  onSaved: (vendor: LogisticsTicketVendor) => void;
};

const validationSchema = yup.object({
  contactName: yup.string(),
  email: yup
    .string()
    .required("Required")
    .test("emails", "Invalid email", (value = "") => {
      const emails = value.split(",").map((email) => email.trim());
      return emails.every((email) => yup.string().email().required().isValidSync(email));
    }),
  phone: yup.string(),
});

export default function VendorContactModal({
  open,
  setOpen,
  vendorUuid,
  vendorName,
  contactName,
  email,
  phone,
  onSaved,
}: Props) {
  const { updatePrimaryContact, submitting } = useVendorContact();
  const hasExisting = Boolean(contactName);

  const { handleSubmit, getFieldProps, touched, errors, isValid, resetForm } = useFormik({
    enableReinitialize: true,
    initialValues: {
      contactName: contactName ?? "",
      email: email ?? "",
      phone: phone ?? "",
    } satisfies VendorContactPayload,
    validationSchema,
    onSubmit: (values) => {
      updatePrimaryContact(vendorUuid, values).then((vendor) => {
        onSaved(vendor);
        setOpen(false);
        resetForm();
      });
    },
  });

  return (
    <Modal
      open={open}
      setOpen={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) {
          resetForm();
        }
      }}
      width="520px"
      style={{ padding: "24px 28px 28px" }}
    >
      <Typography variant="h6" fontWeight={700} sx={{ fontSize: 20, mb: 0.5 }}>
        {hasExisting ? "Edit" : "Add"} Primary Contact
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        {vendorName}
      </Typography>

      <Box component="form" onSubmit={handleSubmit} noValidate autoComplete="off">
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 2 }}>
          <FormControl variant="standard" fullWidth>
            <InputLabel shrink htmlFor="contactName">
              Contact Name
            </InputLabel>
            <InputBase
              id="contactName"
              {...getFieldProps("contactName")}
              helperText={touched.contactName && errors.contactName}
              error={touched.contactName && !!errors.contactName}
            />
          </FormControl>
          <FormControl variant="standard" fullWidth>
            <InputLabel shrink htmlFor="email">
              Email
            </InputLabel>
            <InputBase
              id="email"
              placeholder="email@example.com,other@example.com"
              {...getFieldProps("email")}
              helperText={touched.email && errors.email}
              error={touched.email && !!errors.email}
            />
          </FormControl>
          <FormControl variant="standard" fullWidth>
            <InputLabel shrink htmlFor="phone">
              Phone Number
            </InputLabel>
            <InputBase
              id="phone"
              {...getFieldProps("phone")}
              helperText={touched.phone && errors.phone}
              error={touched.phone && !!errors.phone}
            />
          </FormControl>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 3 }}>
          <Button
            variant="outlined"
            onClick={() => setOpen(false)}
            disabled={submitting}
            sx={{ textTransform: "uppercase", fontWeight: 700 }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting || !isValid}
            sx={{ textTransform: "uppercase", fontWeight: 700 }}
          >
            Save
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
